import React, { useState } from "react";

import { useToken } from "../../context/TokenContext";
import { useBooking } from "../../context/BookingContext";

import {register} from "../../services/authService";
import { CountryCode, Gender } from "../../utils/Constants";
import { SectionHeading } from "../SectionHeading";
import { NameInput } from "./NameInput";
import { EmailInput } from './EmailInput';
import { GenderPicker } from "./GenderPicker";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

interface RegisterFormProps {
  phoneNumber: string;
  countryCode: CountryCode;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
                                                     phoneNumber,
                                                     countryCode
                                                   }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const from = state?.from || '/';

  const { registerToken, setTokens } = useToken();
  const { setBookingUserInfo } = useBooking();
  const title = t('Register');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState(Gender.MALE);
  const isValid = !!gender; // Example validation: true if gender is selected
  const [firstNameValid, setFirstNameValid] = useState(true);
  const [lastNameValid, setLastNameValid] = useState(true);
  const [emailValid, setEmailValid] = useState(true);

  const handleValidate = () => {
    // Track validation status
    let isFormValid = true;

    // First name validation
    const isFirstNameValid = !!firstName.trim();
    setFirstNameValid(isFirstNameValid);
    if (!isFirstNameValid) isFormValid = false;

    // Last name validation
    const isLastNameValid = !!lastName.trim();
    setLastNameValid(isLastNameValid);
    if (!isLastNameValid) isFormValid = false;

    // Email validation with basic format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = !!email && emailRegex.test(email);
    setEmailValid(isEmailValid);
    if (!isEmailValid) isFormValid = false;

    // Gender validation
    if (!gender) isFormValid = false;

    return isFormValid;
    // return false;
  }

  const handleRegister = async () => {
    if (isSubmitting) return;
    if (!handleValidate()) return;
    try {
      if (!firstName || !lastName || !email || !gender || !registerToken) {
        console.error("All fields are required"); // Assuming setError is a state setter for displaying errors
        return;
      }
      setIsSubmitting(true);
      const result = await (register({firstName, lastName, email, gender, registerToken}));
      const { nLoginToken, nRefreshToken, serviceToken, loginExpireAt, refreshExpireAt } = result;

      setTokens(nLoginToken, nRefreshToken, serviceToken, loginExpireAt, refreshExpireAt);
      setBookingUserInfo(phoneNumber, countryCode);

      // console.log('Success');
      navigate(from, {
        replace: true,
        state: {
          ...(location.state || {}),
          phoneNumber,
          toastMessage: t('SignupSuccess'),
          toastMode: 'success',
        }
      });
    } catch (error) {
      console.error("Verification failed", error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 1000);
    }
  };

  return (
    <div className="flex items-center justify-center my-10">
      <div className={`w-full md:w-[40%] p-8 rounded-lg`}>
        {/* Section Heading */}
        <SectionHeading size="lg">{title}</SectionHeading>
        <GenderPicker
          gender={gender}
          setGender={setGender}
          isValid={isValid}
          errorMessage={t('SignupGenderError')}
        />
        <NameInput
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          isFirstNameValid={firstNameValid}
          isLastNameValid={lastNameValid}
          firstNameErrorMessage={t('SignupFirstNameError')}
          lastNameErrorMessage={t('SignupLastNameError')}
        />
        <EmailInput
          email={email}
          setEmail={setEmail}
          isValid={emailValid}
          errorMessage={t('SignupEmailError')}
        />

        {/* Login button / Register button */}
        <button
          className={`w-full py-3 ${
            isSubmitting
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600"
          } text-white font-bold rounded-lg transition-colors`}
          onClick={handleRegister}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
              {t('AuthProcessing')}
          </span>
          ) : (
            title
          )}
        </button>
      </div>
    </div>
  );
};

export default RegisterForm;